import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowRight,
  Building2,
  Users,
  Database,
  TrendingUp,
  Shield,
  Zap,
  CheckCircle2,
  BarChart3,
  Settings,
  Cloud,
  Lock,
  Layers,
  GitBranch
} from "lucide-react";

const SolucionesEmpresariales = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Soluciones Empresariales",
    "provider": {
      "@type": "Organization",
      "name": "erm-media"
    },
    "description": "Sistemas empresariales robustos y escalables: ERP, CRM, sistemas de gestión, automatización de procesos y software a medida.",
    "serviceType": "Desarrollo de Software Empresarial",
    "areaServed": "ES"
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Soluciones Empresariales"
        description="Desarrollamos sistemas empresariales robustos y escalables que optimizan procesos: ERP, CRM, sistemas de gestión, automatización y software a medida para tu empresa."
        keywords="soluciones empresariales, ERP, CRM, sistemas de gestión, automatización de procesos, software empresarial, sistemas corporativos"
        canonicalUrl="https://erm-media.com/soluciones-empresariales"
        structuredData={structuredData}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-background to-primary/5 pt-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Building2 className="w-4 h-4" />
              <span className="text-sm font-medium">Tecnología Empresarial</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Soluciones Empresariales
              <span className="text-primary block">que impulsan tu negocio</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
              Desarrollamos sistemas empresariales robustos y escalables que optimizan procesos,
              mejoran la productividad y transforman la forma en que tu empresa opera.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contacto">
                <Button size="lg" className="group">
                  Solicitar Consultoría
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <a href="#tipos">
                <Button size="lg" variant="outline">
                  Ver Soluciones
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ¿Qué son las soluciones empresariales? */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
              ¿Qué son las Soluciones Empresariales?
            </h2>
            <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
              <p>
                Las <strong className="text-foreground">soluciones empresariales</strong> son sistemas de software diseñados
                específicamente para satisfacer las necesidades complejas de organizaciones y empresas. Estos sistemas integran
                procesos, datos y tecnologías para mejorar la eficiencia operativa y facilitar la toma de decisiones estratégicas.
              </p>
              <p>
                A diferencia del software genérico, las soluciones empresariales se adaptan a los flujos de trabajo únicos de
                cada organización, escalando junto con el crecimiento del negocio y evolucionando con sus necesidades cambiantes.
              </p>
              <p>
                En <strong className="text-foreground">erm-media</strong>, desarrollamos soluciones que no solo automatizan
                tareas, sino que transforman completamente la manera en que tu empresa gestiona información, colabora
                internamente y se relaciona con sus clientes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tipos de Soluciones */}
      <section id="tipos" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Tipos de Soluciones que Desarrollamos
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Creamos sistemas personalizados que se ajustan a las necesidades específicas de tu negocio
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Database className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Sistemas ERP</CardTitle>
                <CardDescription>
                  Enterprise Resource Planning
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Gestión integrada de todos los recursos empresariales: finanzas, inventario,
                  producción, recursos humanos y más, en una sola plataforma.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Sistemas CRM</CardTitle>
                <CardDescription>
                  Customer Relationship Management
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Gestión completa de relaciones con clientes, seguimiento de ventas,
                  automatización de marketing y análisis de comportamiento.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Settings className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Software a Medida</CardTitle>
                <CardDescription>
                  Soluciones Personalizadas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Desarrollo de aplicaciones específicas para resolver problemas únicos de tu
                  empresa, diseñadas exactamente como las necesitas.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Business Intelligence</CardTitle>
                <CardDescription>
                  Análisis y Reportes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Herramientas de análisis de datos, dashboards interactivos y reportes
                  automatizados para toma de decisiones basada en datos.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Cloud className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Portales Web</CardTitle>
                <CardDescription>
                  Plataformas Corporativas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Intranets, extranets y portales de clientes que centralizan información
                  y facilitan la colaboración interna y externa.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <GitBranch className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Integraciones</CardTitle>
                <CardDescription>
                  Conectividad Total
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Conectamos tus sistemas existentes mediante APIs y servicios web para
                  crear un ecosistema tecnológico cohesivo.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Beneficios de Implementar Soluciones Empresariales
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Transforma tu empresa con tecnología que realmente marca la diferencia
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Mayor Productividad
                  </h3>
                  <p className="text-muted-foreground">
                    Automatiza tareas repetitivas y libera tiempo de tu equipo para actividades
                    estratégicas de mayor valor. Reduce errores humanos y acelera procesos.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Decisiones Informadas
                  </h3>
                  <p className="text-muted-foreground">
                    Accede a datos en tiempo real y reportes detallados que te permiten tomar
                    decisiones basadas en información precisa y actualizada.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Mayor Seguridad
                  </h3>
                  <p className="text-muted-foreground">
                    Protege la información crítica de tu empresa con controles de acceso,
                    encriptación y auditorías completas de todas las operaciones.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Layers className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Escalabilidad
                  </h3>
                  <p className="text-muted-foreground">
                    Sistemas que crecen con tu empresa. Añade funcionalidades, usuarios y
                    capacidad según tus necesidades sin limitaciones.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Procesos Estandarizados
                  </h3>
                  <p className="text-muted-foreground">
                    Establece procedimientos consistentes en toda la organización, mejorando
                    la calidad y reduciendo la variabilidad en los resultados.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Lock className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Cumplimiento Normativo
                  </h3>
                  <p className="text-muted-foreground">
                    Asegura el cumplimiento de regulaciones y normativas específicas de tu
                    industria con controles y trazabilidad incorporados.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metodología */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Nuestra Metodología de Desarrollo
              </h2>
              <p className="text-lg text-muted-foreground">
                Un proceso probado que garantiza el éxito de tu proyecto
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Análisis y Consultoría
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Comenzamos con un análisis profundo de tus procesos actuales, identificando
                    puntos de dolor, oportunidades de mejora y requisitos específicos. Trabajamos
                    contigo para entender completamente tu negocio.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Diseño y Arquitectura
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Diseñamos la arquitectura técnica y la experiencia de usuario, creando prototipos
                    interactivos que te permiten visualizar la solución antes de desarrollarla.
                    Validamos cada decisión contigo.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Desarrollo Ágil
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Desarrollamos en sprints iterativos, entregando funcionalidad de forma incremental.
                    Esto te permite ver avances constantes, proporcionar feedback y ajustar el rumbo
                    cuando sea necesario.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    4
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Testing y Calidad
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Realizamos pruebas exhaustivas en cada etapa: unitarias, de integración, de
                    rendimiento y de usuario. Garantizamos que el sistema funcione perfectamente
                    antes del lanzamiento.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    5
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Implementación y Capacitación
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Desplegamos el sistema en tu infraestructura y capacitamos a tu equipo para su uso
                    efectivo. Proporcionamos documentación completa y soporte durante la transición.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    6
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Soporte y Evolución
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    No terminamos con el lanzamiento. Proporcionamos soporte continuo, mantenimiento
                    y desarrollo de nuevas funcionalidades según tu negocio evoluciona.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industrias */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Industrias que Servimos
              </h2>
              <p className="text-lg text-muted-foreground">
                Experiencia en múltiples sectores con soluciones adaptadas
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: "Retail y Comercio",
                  description: "Sistemas de punto de venta, gestión de inventario, e-commerce integrado y análisis de ventas."
                },
                {
                  title: "Manufactura",
                  description: "Control de producción, gestión de cadena de suministro, trazabilidad y optimización de recursos."
                },
                {
                  title: "Servicios Profesionales",
                  description: "Gestión de proyectos, facturación, control de tiempo y colaboración entre equipos."
                },
                {
                  title: "Logística",
                  description: "Gestión de flotas, seguimiento de envíos, optimización de rutas y gestión de almacenes."
                },
                {
                  title: "Salud",
                  description: "Historiales médicos electrónicos, gestión de citas, facturación y cumplimiento normativo."
                },
                {
                  title: "Educación",
                  description: "Plataformas de aprendizaje, gestión académica, comunicación y análisis de rendimiento."
                }
              ].map((industry, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">{industry.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      {industry.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section id="contacto" className="py-20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              ¿Listo para Transformar tu Empresa?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Agenda una consultoría gratuita y descubre cómo nuestras soluciones empresariales
              pueden impulsar tu negocio al siguiente nivel.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/#contacto">
                <Button size="lg" className="group">
                  Solicitar Consultoría Gratuita
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <a href="/">
                <Button size="lg" variant="outline">
                  Ver Más Servicios
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolucionesEmpresariales;
